import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { ActivityMediaInterface, FileTypeEnum } from '@sport-activities/sdk';
import { of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { MimeTypeEnum } from '~/component/enums/mime-type.enum';
import {
  Upload,
  UploadCollectionChanges,
} from '~/component/upload-list/upload-list.component';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { FileExtensionValidator } from '~/core/validators/file-extension/file-extension.validator';
import { FileExtensionEnum as FileExtension } from '~/organizer/enums/file-extention.enum';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';

@Component({
  selector: 'app-organizer-activity-edition-uploads',
  templateUrl: './uploads.component.html',
})
export class OrganizerActivityEditionUploadsComponent
  implements OnInit, OnDestroy
{
  private readonly subscriptions = new Subscription();
  private attachmentCollection: ActivityMediaInterface[];

  public activity: Activity;
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public mimeType = MimeTypeEnum;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public uploadCollection: Upload[] = [];
  public uploads = new FormControl();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly router: Router,
    private readonly localizeRouterService: LocalizeRouterService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;
        this.attachmentCollection = this.activity.bookingAttachments;
        this.attachmentCollection?.forEach((attachment) => {
          const { name, size, identifier } = attachment;
          this.uploadCollection.push({
            name,
            size,
            identifier,
          });
        });
      })
    );

    const allowedExtensions = [FileExtension.PDF];

    this.uploads.addValidators(FileExtensionValidator(allowedExtensions));

    this.subscriptions.add(
      this.uploads.valueChanges.subscribe(() => {
        this.isFormValid =
          this.uploads.value.added?.length > 0 ||
          this.uploads.value.removed?.length > 0;
      })
    );
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.activity?.bookingAttachments?.length);
  }

  public getErrors(): ValidationErrors | null {
    return !this.isFormValid && this.uploads.errors;
  }

  public updateUploads(): void {
    if (this.uploads.errors) {
      this.isFormValid = false;
      return;
    }

    const { added, removed } = this.uploads.value as UploadCollectionChanges;

    if (added.length === 0 && removed.length === 0) {
      const translatedPath = this.localizeRouterService.translateRoute([
        '/organizer.activity.manage',
        this.activity.sku,
        'organizer.activity-edition.edition',
      ]) as [];

      this.router.navigate(translatedPath);

      return;
    }

    this.isOverlayDisplayed = true;

    if (removed.length > 0) {
      removed.forEach((fileToDelete) => {
        return this.organizerSharedActivityService
          .deleteMedia(this.activity.sku, fileToDelete.identifier)
          .subscribe(() => {
            if (added.length === 0) {
              this.overlayStatus = SuccessStatusEnum.SUCCESS;
            }
          });
      });
    }

    if (added.length > 0) {
      added.forEach((upload) => {
        const file = upload.file;

        if (file.size === 0) {
          return of(undefined);
        }

        return this.organizerSharedActivityService
          .addMedia(this.activity.sku, file, file.name, [
            'BOOKING_MAIL_ATTACHMENT' as FileTypeEnum,
          ])
          .pipe(
            mergeMap(() =>
              this.organizerSharedActivityService.getActivity(
                this.activity?.sku?.toString()
              )
            )
          )
          .subscribe(() => {
            this.overlayStatus = SuccessStatusEnum.SUCCESS;
          });
      });
    }
  }
}
